import RenderPipeline from "../renderpipeline";
import ShaderService from "../../shader/shader_service";
import Camera from "../../scene/camera";
import ResourceService from "../../resource/resourceservice";
import Transform from "../transform";
import JumpGraphService from "../../collision/jump_graph_service";
import { JumpAnimKind, JumpEdge, JumpNode } from "../../collision/jump_graph_types";
import { RENDER_SAMPLE_COUNT } from "../render_config";

export default class GraphRenderPipeline extends RenderPipeline {
  label: string;
  shader_name: string;
  graph_service: JumpGraphService;

  camera_buffer: GPUBuffer | null = null;
  bind_group: GPUBindGroup | null = null;

  vertex_buffer: GPUBuffer | null = null;
  vertex_capacity_floats = 0;
  vertex_count = 0;

  constructor(key: string, shader_name: string, graph_service: JumpGraphService) {
    super(key + "_graph");
    this.label = key + "_graph";
    this.shader_name = shader_name;
    this.graph_service = graph_service;
  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    _resource_service: ResourceService
  ): Promise<void> {
    const shader = shader_service.get_render(this.shader_name);

    this.pipeline = device.createRenderPipeline({
      layout: "auto",
      label: this.label,
      vertex: {
        module: shader,
        entryPoint: "vs_main",
        buffers: [
          {
            arrayStride: 28,
            attributes: [
              { shaderLocation: 0, offset: 0, format: "float32x3" },
              { shaderLocation: 1, offset: 12, format: "float32x4" },
            ],
          },
        ],
      },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [
          {
            format,
            blend: {
              color: {
                operation: "add",
                srcFactor: "src-alpha",
                dstFactor: "one-minus-src-alpha",
              },
              alpha: {
                operation: "add",
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
              },
            },
          },
        ],
      },
      primitive: {
        topology: "line-list",
        cullMode: "none",
      },
      depthStencil: {
        format: "depth24plus",
        depthWriteEnabled: false,
        depthCompare: "less",
      },
      multisample: {
        count: RENDER_SAMPLE_COUNT,
      },
    });

    this.camera_buffer = device.createBuffer({
      size: 64,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.bind_group = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      label: this.label + "_bind_group",
      entries: [{ binding: 0, resource: { buffer: this.camera_buffer } }],
    });

    this.ensure_vertex_capacity(device, 4096);
  }

  private ensure_vertex_capacity(device: GPUDevice, required_float_count: number): void {
    if (this.vertex_buffer && this.vertex_capacity_floats >= required_float_count) {
      return;
    }

    let new_capacity = Math.max(4096, this.vertex_capacity_floats || 4096);
    while (new_capacity < required_float_count) {
      new_capacity *= 2;
    }

    this.vertex_capacity_floats = new_capacity;

    this.vertex_buffer = device.createBuffer({
      label: this.label + "_vertex_buffer",
      size: this.vertex_capacity_floats * 4,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
  }

  prepare(device: GPUDevice, camera: Camera, _transform: Transform): void {
    if (!this.camera_buffer) return;

    device.queue.writeBuffer(
      this.camera_buffer,
      0,
      camera.view_projection_matrix.buffer as ArrayBuffer,
      camera.view_projection_matrix.byteOffset,
      camera.view_projection_matrix.byteLength
    );

    const verts: number[] = [];

    for (const node of this.graph_service.get_nodes()) {
      this.append_node(verts, node);
    }
    for (const edge of this.graph_service.get_edges()) {
      this.append_edge(verts, edge);
    }

    // inside prepare(), after appending base nodes and edges:
const highlight = this.graph_service.get_highlighted_path();
if (highlight) {
  for (const edge of highlight) {
    this.append_highlight_edge(verts, edge);
  }
  // also highlight the waypoint nodes (endpoints)
  const ids = new Set<string>();
  for (const e of highlight) {
    ids.add(e.from);
    ids.add(e.to);
  }
  for (const id of ids) {
    const n = this.graph_service.get_node(id);
    if (n) this.append_highlight_node(verts, n);
  }
}




    const data = new Float32Array(verts);
    this.vertex_count = data.length / 7;

    if (data.length === 0) return;

    this.ensure_vertex_capacity(device, data.length);
    if (!this.vertex_buffer) return;

    device.queue.writeBuffer(
      this.vertex_buffer,
      0,
      data.buffer,
      data.byteOffset,
      data.byteLength
    );
  }

  private append_highlight_edge(out: number[], edge: JumpEdge): void {
  const a = this.graph_service.get_node(edge.from);
  const b = this.graph_service.get_node(edge.to);
  if (!a || !b) return;

  const color: [number, number, number, number] = [1.0, 1.0, 0.0, 1.0]; // bright yellow
  const lift = 0.30; // higher than the base edge lift (0.18) to draw on top
  const jog = 0.05;

  let ax = a.position[0], ay = a.position[1] + lift, az = a.position[2];
  let bx = b.position[0], by = b.position[1] + lift, bz = b.position[2];

  if (a.kind === "ground") { ax = bx + jog; ay = 0.02; az = bz + jog; }
  if (b.kind === "ground") { bx = ax + jog; by = 0.02; bz = az + jog; }

  // draw the highlight as a small triple-stripe for visual weight
  const offsets: Array<[number, number]> = [
    [0, 0],
    [0.02, 0],
    [-0.02, 0],
  ];
  for (const [ox, oz] of offsets) {
    this.push_line(out, ax + ox, ay, az + oz, bx + ox, by, bz + oz, ...color);
  }
}

private append_highlight_node(out: number[], node: JumpNode): void {
  if (node.kind === "ground") return;

  const color: [number, number, number, number] = [1.0, 1.0, 0.0, 1.0]; // yellow
  const c = node.position;
  const lift = 0.30;
  const s = 0.14; // bigger than base crosshair (0.08)

  // bigger crosshair at higher lift
  this.push_line(out, c[0]-s, c[1]+lift, c[2],   c[0]+s, c[1]+lift, c[2],   ...color);
  this.push_line(out, c[0],   c[1]+lift-s, c[2], c[0],   c[1]+lift+s, c[2], ...color);
  this.push_line(out, c[0],   c[1]+lift, c[2]-s, c[0],   c[1]+lift, c[2]+s, ...color);

  // diagonal cross for extra weight
  this.push_line(out, c[0]-s*0.7, c[1]+lift, c[2]-s*0.7, c[0]+s*0.7, c[1]+lift, c[2]+s*0.7, ...color);
  this.push_line(out, c[0]-s*0.7, c[1]+lift, c[2]+s*0.7, c[0]+s*0.7, c[1]+lift, c[2]-s*0.7, ...color);
}

  draw(pass: GPURenderPassEncoder): void {
    if (!this.pipeline || !this.bind_group || !this.vertex_buffer) return;
    if (this.vertex_count <= 0) return;

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind_group);
    pass.setVertexBuffer(0, this.vertex_buffer);
    pass.draw(this.vertex_count);
  }

  // --- Node + edge geometry --------------------------------------------

  private append_node(out: number[], node: JumpNode): void {
  if (node.kind === "ground") return;

  const color: [number, number, number, number] = [1.0, 0.4, 1.0, 1.0];
  const c = node.position;
  const lift = 0.18;
  const s = 0.08;

  // crosshair at lifted height
  this.push_line(out, c[0]-s, c[1]+lift, c[2],   c[0]+s, c[1]+lift, c[2],   ...color);
  this.push_line(out, c[0],   c[1]+lift-s, c[2], c[0],   c[1]+lift+s, c[2], ...color);
  this.push_line(out, c[0],   c[1]+lift, c[2]-s, c[0],   c[1]+lift, c[2]+s, ...color);

  // stem connecting cap-top to the lifted crosshair
  this.push_line(out, c[0], c[1], c[2], c[0], c[1]+lift, c[2], ...color);
}

private append_edge(out: number[], edge: JumpEdge): void {
  const a = this.graph_service.get_node(edge.from);
  const b = this.graph_service.get_node(edge.to);
  if (!a || !b) return;

  const color = this.color_for_anim(edge.anim);
  const lift = 0.18;
  const jog = 0.05;

  let ax = a.position[0], ay = a.position[1] + lift, az = a.position[2];
  let bx = b.position[0], by = b.position[1] + lift, bz = b.position[2];

  if (a.kind === "ground") { ax = bx + jog; ay = 0;          az = bz + jog; }
  if (b.kind === "ground") { bx = ax + jog; by = 0;          bz = az + jog; }

  this.push_line(out, ax, ay, az, bx, by, bz, ...color);
}
  
  

  private color_for_anim(anim: JumpAnimKind): [number, number, number, number] {
    switch (anim) {
      case "object_to_object_across":    return [1.0, 1.0, 1.0, 1.0]; // white
      case "object_to_object_up":        return [0.2, 1.0, 0.3, 1.0]; // green
      case "object_to_object_down_low":  return [1.0, 0.9, 0.2, 1.0]; // yellow
      case "object_to_object_down_high": return [1.0, 0.5, 0.1, 1.0]; // orange
      case "ground_to_object":           return [0.3, 0.8, 1.0, 1.0]; // sky blue
      case "object_to_ground":           return [1.0, 0.3, 0.6, 1.0]; // pink
    }
  }

  private push_line(
    out: number[],
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    r: number, g: number, b: number, a: number
  ): void {
    out.push(ax, ay, az, r, g, b, a);
    out.push(bx, by, bz, r, g, b, a);
  }
}
